import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS, Track } from '@bariendo/lib/constants';
import { useTwilioPhone } from '@/hooks/useTwilioPhone';
import { alert } from '@/services/alert';
import { getContactLabel, getPrimaryPhone } from '@/services/contact-point';
import { formatBirthDate, getPreferredLanguageDisplay } from '@/services/patient';
import { EnabledTopic } from '@/services/timeline-threads';
import {
  BariatricProcedureStage,
  BariatricProcedureStageLabel,
  CommunicationThreadTopic,
} from '@bariendo/lib/constants';
import { usePatientProcedureStage } from '@/hooks/usePatientProcedureStage';
import { usePatientTrack } from '@/hooks/usePatientTrack';
import { TrackPill } from './TrackPill';
import { BARIATRIC_PROCEDURE_STAGE_EXTENSION } from '@bariendo/lib/extensions';
import { Badge, Flex, Group, Modal, Stack, Text, Tooltip } from '@mantine/core';
import { formatHumanName, getExtension, getReferenceString } from '@medplum/core';
import { ContactPoint, Patient } from '@medplum/fhirtypes';
import { ResourceAvatar } from '@medplum/react';
import { OFFERING_COLORS, getOfferingCode, isIgbRemoval } from './offeringColors';
import {
  IconCalendarPlus,
  IconClipboardText,
  IconDeviceMobileMessage,
  IconMessage,
  IconCreditCard,
  IconMicroscope,
  IconCheckbox,
  IconNote,
  IconPhoneOutgoing,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { trackCallInitiated } from '@/services/analytics';
import { useTimelineContext } from '../hooks/useTimelineContext';
import { getPatientPhones } from '../services/timelinePatient';
import { MessageBoxType } from '@/components/Timeline/types';
import { ActionButtonGroup } from '@/components/Timeline/ActionButtonGroup';
import TimelineHeaderAddMenu from '@/components/Timeline/TimelineHeader/TimelineHeaderAddMenu';
import { RxIcon } from '@/components/icons/RxIcon';
import { TimelineHeaderSkeleton } from '@/components/TimelineSkeleton';
import { CreateFaxForm } from '@/pages/FaxingPage/CreateFaxForm';
import { PaymentOrderDrawer } from '@/components/PaymentOrderDrawer/PaymentOrderDrawer';
import { PaymentOrderState } from '@/hooks/usePaymentOrder';
import { OutboundIcon } from '@/components/icons/OutboundIcon';
import TimelineHeaderAddMenuItem from '@/components/Timeline/TimelineHeader/TimelineHeaderAddMenuItem';
import { billingMenuRows } from '@/services/billing-menu';
import { getStoredPaymentStatus } from '@bariendo/lib/payment-status';
import { RefundHistoryEntry } from '@/services/Timeline/refundCards';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';
import PatientActionsMenu from './PatientActionsMenu';
import { PatientNameEditor } from './PatientNameEditor';
import { headerNameParts } from '@/services/patient-name';

type PatientTimelineHeaderProps = {
  patient?: Patient;
  enabledTopics: EnabledTopic[];
  onAddAppointment?: () => void;
  /**
   * From the page's single usePaymentOrder instance. Threaded rather than read
   * here so the drawer and the timeline card share one source — a second hook
   * instance would refresh the drawer and leave the card stale.
   */
  paymentOrder: PaymentOrderState;
  /**
   * Lifted to the page because the warm hand-off card opens this drawer too, and
   * the card lives in the timeline — a sibling of this header, not a child.
   */
  isPaymentOrderOpened: boolean;
  setIsPaymentOrderOpened: (opened: boolean) => void;
};

export function PatientTimelineHeader({
  patient,
  enabledTopics,
  onAddAppointment,
  paymentOrder,
  isPaymentOrderOpened,
  setIsPaymentOrderOpened,
}: PatientTimelineHeaderProps) {
  const { patientContext, messageBoxContext, medicationsContext, appointmentsContext, refundRequestsContext } =
    useTimelineContext();
  const patientLoading = patientContext?.patientLoading;
  const { setMessageBoxVariant, setShowMessageInput, setSelectedPhone, setTopic } = messageBoxContext;

  const { makeCall } = useTwilioPhone();
  const posthog = usePostHog();

  const patientPhones = useMemo(() => (patient ? getPatientPhones(patient) : []), [patient]);
  const primaryPhone = useMemo(() => getPrimaryPhone(patientPhones), [patientPhones]);

  const [isFaxModalOpened, setIsFaxModalOpened] = useState(false);

  function handleShowSmsMessageBox(phone?: ContactPoint) {
    const selectedPhone = phone ?? primaryPhone;
    if (!selectedPhone) {
      alert.error("This patient doesn't have a phone number on file.");
      return;
    }

    setMessageBoxVariant(MessageBoxType.SMS);
    setSelectedPhone(selectedPhone);
    setShowMessageInput(true);
  }

  // The coverage composer, inline at the top of the timeline (BAR-3076).
  function handleShowCoverageActivity() {
    setMessageBoxVariant(MessageBoxType.COVERAGE_ACTIVITY);
    setShowMessageInput(true);
  }

  // The refund or cancellation request, inline the same way (BAR-3069).
  function handleShowRefundRequest() {
    setMessageBoxVariant(MessageBoxType.REFUND_REQUEST);
    setShowMessageInput(true);
  }

  function handleShowChatMessageBox(topic?: CommunicationThreadTopic) {
    setTopic(topic ?? CommunicationThreadTopic.CLINICAL);
    setMessageBoxVariant(MessageBoxType.CHAT);
    setShowMessageInput(true);
  }

  function handleShowNoteBox() {
    setMessageBoxVariant(MessageBoxType.STANDALONE_NOTE);
    setShowMessageInput(true);
  }

  function handleShowTaskBox() {
    setMessageBoxVariant(MessageBoxType.TASK);
    setShowMessageInput(true);
  }

  function handleShowEncounterChart() {
    setMessageBoxVariant(MessageBoxType.ENCOUNTER);
    setShowMessageInput(true);
    // TODO: to be implemented in the BAR-1311
  }

  function handleShowLabOrderBox() {
    setMessageBoxVariant(MessageBoxType.LAB_ORDER);
    setShowMessageInput(true);
  }

  const name = useMemo(() => (patient?.name ? formatHumanName(patient.name[0]) : 'Unknown Patient'), [patient?.name]);
  // Legal name with the preferred name in quotes after the first name (BAR-2993).
  const nameParts = useMemo(() => headerNameParts(patient), [patient]);
  // Held per patient: the route does not remount on a patient switch, and an
  // editor prefilled with one patient's names must never save to the next.
  const [editingNameFor, setEditingNameFor] = useState<string>();

  const photoUrl = useMemo(() => patient?.photo?.[0]?.url, [patient?.photo]);

  const storedStage = useMemo(() => {
    if (!patient) return undefined;
    return getExtension(patient, BARIATRIC_PROCEDURE_STAGE_EXTENSION)?.valueString as
      | BariatricProcedureStage
      | undefined;
  }, [patient]);

  // Most patients have no stored stage, so the pill was absent rather than wrong.
  const computedStage = usePatientProcedureStage(patient?.id, Boolean(storedStage));
  const stage = storedStage ?? computedStage;
  const procedureStage = stage ? BariatricProcedureStageLabel[stage] : undefined;

  const preferredLanguage = useMemo(
    () => getPreferredLanguageDisplay(patient?.communication),
    [patient?.communication]
  );

  // Go-to-market / track pill — resolved from the patient's active EpisodeOfCare
  // (BAR-2335). Unresolved (no episode, e.g. not yet backfilled) → no pill; we do
  // not default to Self-pay so nobody is mislabeled pre-backfill.
  const { track, loading: trackLoading } = usePatientTrack(patient?.id);

  // DoB + age — per the patient-display canon, Line 1 of the identity stack
  // shows "Name · MMM D, YYYY (NY)". Delegated to the shared formatBirthDate
  // helper in services/patient.ts so validation and formatting stay
  // single-sourced. We still null-check upstream to hide the "·" separator
  // when birthDate is missing (rather than rendering "· N/A").
  const dobDisplay = patient?.birthDate ? formatBirthDate(patient.birthDate) : null;

  // Per-offering pill — find the latest non-IGB-removal procedure appointment
  // and map its offering code to the canonical saturated brand color. If the
  // patient has no procedures (or only IGB removals), the pill is not rendered.
  // Procedures come from the lifted appointmentsContext on TimelineContext;
  // PatientSummary's PatientProcedures consumes the same source.
  const procedures = appointmentsContext?.procedures ?? [];
  const offering = useMemo(() => {
    const latestPrimary = procedures.find((appt) => !isIgbRemoval(appt.appointmentType?.coding?.[0]?.display));
    if (!latestPrimary) return null;
    return getOfferingCode(latestPrimary.appointmentType?.coding?.[0]?.display);
  }, [procedures]);

  if (patientLoading || !patient) {
    return <TimelineHeaderSkeleton />;
  }

  const isEditingName = editingNameFor !== undefined && editingNameFor === patient.id;

  // Prepare dropdown items for chat topics
  const chatTopicItems = enabledTopics.map((topic) => ({
    key: topic.code,
    label: topic.display,
    onClick: () => handleShowChatMessageBox(topic.code as CommunicationThreadTopic),
  }));

  // Prepare dropdown items for phone numbers (SMS)
  const smsPhoneItems = patientPhones.map((phone) => ({
    key: phone.value!,
    label: getContactLabel(phone),
    caption: formatPhoneNumber(phone.value),
    onClick: () => handleShowSmsMessageBox(phone),
  }));

  // Prepare dropdown items for phone numbers (Calls)
  const callPhoneItems = patientPhones.map((phone) => ({
    key: phone.value!,
    label: getContactLabel(phone),
    caption: phone.value,
    onClick: () => {
      // Track call initiated from patient timeline header
      if (patient?.id) {
        trackCallInitiated(posthog, {
          source: 'patient timeline header',
          patient_id: patient.id,
        });
      }
      const recipient = patient?.id ? getReferenceString(patient) : undefined;
      makeCall(phone.value!, undefined, recipient);
    },
  }));

  return (
    <Flex justify="space-between" align="center" px="md" py="sm" w="100%">
      <Flex gap="md" align="center" miw={0}>
        <ResourceAvatar
          src={photoUrl}
          value={patient}
          size="lg"
          radius={9999}
          alt={name}
          styles={{
            root: {
              borderRadius: '50%',
              boxShadow: '0 0 0 2px var(--ev-color-warm-white, #fafcf7)',
              overflow: 'hidden',
              backgroundColor: 'rgba(36, 55, 33, 0.10)',
            },
            placeholder: {
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              fontWeight: 500,
              color: 'var(--ev-color-green-dark, #243721)',
            },
          }}
        />
        <Stack gap={4} miw={0}>
          {/* Line 1 — name + DoB · Age (patient-display canon); Edit name replaces it */}
          {isEditingName ? (
            <PatientNameEditor
              key={patient.id}
              patient={patient}
              onClose={() => {
                // Only this patient's editor: a save that settles after a switch must not close the next one.
                const closing = patient.id;
                setEditingNameFor((id) => (id === closing ? undefined : id));
              }}
            />
          ) : (
            <Group gap={6} align="baseline" wrap="nowrap" miw={0}>
              {/* A long name ellipsizes at its end, so the quoted part near the start stays.
                  `clip`, not `hidden`: a hidden box is a scroll container (see CLAUDE.md). */}
              <Text
                variant="header"
                miw={0}
                style={{ whiteSpace: 'nowrap', overflow: 'clip', textOverflow: 'ellipsis' }}
              >
                {nameParts.preferred ? (
                  <>
                    {nameParts.before && `${nameParts.before} `}
                    <Tooltip label="Preferred name" withArrow>
                      <span>“{nameParts.preferred}”</span>
                    </Tooltip>
                    {nameParts.after && ` ${nameParts.after}`}
                  </>
                ) : (
                  name
                )}
              </Text>
              {dobDisplay && (
                <Text size="sm" c="rgba(0, 0, 0, 0.55)" style={{ flexShrink: 0 }}>
                  · {dobDisplay}
                </Text>
              )}
            </Group>
          )}
          {/* Line 2 — per-offering pill + patient-state pill + language + edit menu */}
          <Group gap="xs" align="center">
            {offering && (
              <Badge
                size="sm"
                variant="filled"
                radius="xl"
                styles={{ root: { backgroundColor: OFFERING_COLORS[offering], color: '#fafcf7' } }}
              >
                {offering}
              </Badge>
            )}
            {procedureStage && (
              <Badge size="sm" color="gray" variant="light" radius="xl">
                {procedureStage}
              </Badge>
            )}
            {preferredLanguage && (
              <Badge size="sm" color="gray" variant="light" radius="xl">
                {preferredLanguage}
              </Badge>
            )}
            {/* Go-to-market pill — self-pay (neutral) vs B2B partner (amber);
                renders nothing until the track resolves */}
            <TrackPill track={track} />
            {/* Edit-patient overflow menu — lives next to identity pills, not in the action bar */}
            {!isEditingName && (
              <PatientActionsMenu patient={patient} onEditName={() => setEditingNameFor(patient.id)} />
            )}
          </Group>
        </Stack>
      </Flex>

      <Group gap="xs">
        <PatientTimelineActions
          patient={patient}
          callPhoneItems={callPhoneItems}
          patientPhones={patientPhones}
          chatTopicItems={chatTopicItems}
          smsPhoneItems={smsPhoneItems}
          onCallInitiated={(patientId) => {
            trackCallInitiated(posthog, {
              source: 'patient timeline header',
              patient_id: patientId,
            });
          }}
          onMakeCall={(phoneNumber) => {
            const recipient = patient?.id ? getReferenceString(patient) : undefined;
            makeCall(phoneNumber, undefined, recipient);
          }}
          getPrimaryPhone={getPrimaryPhone}
          onShowNoteBox={handleShowNoteBox}
          onShowTaskBox={handleShowTaskBox}
          onShowChatMessageBox={handleShowChatMessageBox}
          onShowSmsMessageBox={handleShowSmsMessageBox}
          onShowEncounterChart={handleShowEncounterChart}
          onShowLabOrderBox={handleShowLabOrderBox}
          onShowPaymentOrder={() => setIsPaymentOrderOpened(true)}
          paymentOrder={paymentOrder}
          track={track}
          trackLoading={trackLoading}
          onShowCoverageActivity={handleShowCoverageActivity}
          onShowRefundRequest={handleShowRefundRequest}
          refundRequests={refundRequestsContext?.requests}
          onShowFaxForm={() => setIsFaxModalOpened(true)}
          onShowDoseSpotIFrame={() => medicationsContext?.setShowDoseSpotIFrame(true)}
          onAddAppointment={onAddAppointment}
        />
      </Group>

      <Modal
        opened={isFaxModalOpened}
        onClose={() => setIsFaxModalOpened(false)}
        title="Outgoing fax"
        centered
        size="xl"
      >
        <CreateFaxForm onClose={() => setIsFaxModalOpened(false)} initialPatient={patient} />
      </Modal>

      {/* Mounted only while open so the catalog fetch does not run on every
          patient view. The menu item that opens it is already role- and
          flag-gated, so reaching this at all implies permission. */}
      {patient && isPaymentOrderOpened && (
        <PaymentOrderDrawer
          // Keyed on the patient so the drawer's own state — the selected
          // service, the discounts, and above all the issued payment link —
          // cannot survive a patient switch. This tree is not remounted by the
          // router when :patientId changes, and a stale link beside a live Send
          // button is a wrong-patient payment action.
          key={patient.id}
          patient={patient}
          opened={isPaymentOrderOpened}
          onClose={() => setIsPaymentOrderOpened(false)}
          order={paymentOrder.order}
          orderError={paymentOrder.error}
          onOrderChanged={paymentOrder.refresh}
        />
      )}
    </Flex>
  );
}

type PatientTimelineActionsProps = {
  patient: Patient;
  callPhoneItems: Array<{
    key: string;
    label: string;
    caption: string | undefined;
    onClick: () => void;
  }>;
  chatTopicItems: Array<{
    key: string;
    label: string;
    caption?: string;
    onClick: () => void;
  }>;
  smsPhoneItems: Array<{
    key: string;
    label: string;
    caption: string | undefined;
    onClick: () => void;
  }>;
  onCallInitiated: (patientId: string) => void;
  onMakeCall: (phoneNumber: string) => void;
  getPrimaryPhone: (phones: ContactPoint[]) => ContactPoint | undefined;
  patientPhones: ContactPoint[];
  onShowNoteBox: () => void;
  onShowTaskBox: () => void;
  onShowChatMessageBox: (topic: CommunicationThreadTopic) => void;
  onShowSmsMessageBox: (phone?: ContactPoint) => void;
  onShowEncounterChart: () => void;
  onShowLabOrderBox: () => void;
  onShowFaxForm: () => void;
  onShowDoseSpotIFrame: () => void;
  onAddAppointment?: () => void;
  onShowPaymentOrder: () => void;
  /** Payment request shows only before an order exists (BAR-3074). */
  paymentOrder: Pick<PaymentOrderState, 'order' | 'loading'>;
  track: Track | undefined;
  trackLoading: boolean;
  onShowCoverageActivity: () => void;
  onShowRefundRequest: () => void;
  /** The patient's refund requests (BAR-3069); undefined until read. */
  refundRequests?: RefundHistoryEntry[];
};

export function PatientTimelineActions({
  patient,
  callPhoneItems,
  chatTopicItems,
  patientPhones,
  smsPhoneItems,
  onCallInitiated,
  onMakeCall,
  getPrimaryPhone,
  onShowNoteBox,
  onShowTaskBox,
  onShowSmsMessageBox,
  onShowEncounterChart,
  onShowLabOrderBox,
  onShowFaxForm,
  onShowChatMessageBox,
  onShowDoseSpotIFrame,
  onAddAppointment,
  onShowPaymentOrder,
  paymentOrder,
  track,
  trackLoading,
  onShowCoverageActivity,
  onShowRefundRequest,
  refundRequests,
}: PatientTimelineActionsProps) {
  const { currentUserPermissions } = useCurrentUser();
  const treatmentPaymentsEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_TREATMENT_PAYMENTS);
  // Same gate as every other task surface (patient tab, timeline, inbox).
  const isTaskManagementEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_TASK_MANAGEMENT);

  // Only show medications button if user has prescription write permission
  const canShowMedicationsButton = currentUserPermissions.canWriteMedications;

  // Only show lab order button if feature is enabled AND user has lab write permission
  const canShowLabOrderButton = currentUserPermissions.canWriteLabs;

  // Hidden entirely rather than disabled: a disabled control would advertise
  // payments to someone whose role cannot reach them. The bot enforces the same
  // predicate, so a visible-but-refused button is the failure mode this prevents.
  const canShowPaymentOrderButton = currentUserPermissions.canAccessPayments && treatmentPaymentsEnabled;

  const benefitEventsEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_EMPLOYER_BENEFIT_EVENTS);
  // Track-aware billing rows straight on + Add (BAR-3074); one that doesn't apply is hidden.
  const billingRows = billingMenuRows({
    canAccessPayments: canShowPaymentOrderButton,
    paymentsFlagEnabled: treatmentPaymentsEnabled,
    canRecordCoverage: currentUserPermissions.canRecordBenefitEvents,
    coverageFlagEnabled: benefitEventsEnabled,
    track,
    trackLoading,
    hasOrder: !!paymentOrder.order,
    orderLoading: paymentOrder.loading,
    paymentStatus: getStoredPaymentStatus(patient)?.status,
    refundRequests,
  });

  return (
    <Group gap="xs">
      <TimelineHeaderAddMenu>
        <TimelineHeaderAddMenuItem icon={<IconNote size="14px" />} label="Sticky note" onClick={onShowNoteBox} />
        {isTaskManagementEnabled && (
          <TimelineHeaderAddMenuItem icon={<IconCheckbox size="14px" />} label="Task" onClick={onShowTaskBox} />
        )}
        <TimelineHeaderAddMenuItem
          icon={<IconDeviceMobileMessage size="14px" />}
          label="SMS message"
          dropdownItems={smsPhoneItems}
          onClick={() => onShowSmsMessageBox()}
        />
        <TimelineHeaderAddMenuItem
          icon={<IconMessage size="14px" />}
          label="Chat message"
          dropdownItems={chatTopicItems}
          onClick={() => onShowChatMessageBox(CommunicationThreadTopic.LOGISTICAL)}
        />
        {onAddAppointment && (
          <TimelineHeaderAddMenuItem
            icon={<IconCalendarPlus size="14px" />}
            label="Appointment"
            onClick={onAddAppointment}
          />
        )}
        <TimelineHeaderAddMenuItem
          icon={<IconClipboardText size="14px" />}
          label="Chart note"
          onClick={onShowEncounterChart}
        />

        {canShowLabOrderButton && (
          <TimelineHeaderAddMenuItem
            icon={<IconMicroscope size="14px" />}
            label="Lab order"
            onClick={onShowLabOrderBox}
          />
        )}
        <TimelineHeaderAddMenuItem icon={<OutboundIcon size={14} theme="dark" />} label="Fax" onClick={onShowFaxForm} />
        {canShowMedicationsButton && (
          <TimelineHeaderAddMenuItem icon={<RxIcon />} label="Prescription" onClick={onShowDoseSpotIFrame} />
        )}
        {billingRows.paymentRequest && (
          <TimelineHeaderAddMenuItem
            icon={<IconCreditCard size="14px" />}
            label="Payment request"
            onClick={onShowPaymentOrder}
          />
        )}
        {billingRows.coverageActivity && (
          <TimelineHeaderAddMenuItem
            icon={<IconCreditCard size="14px" />}
            label="Coverage activity"
            onClick={onShowCoverageActivity}
          />
        )}
        {billingRows.refundRequest && (
          <TimelineHeaderAddMenuItem
            icon={<IconCreditCard size="14px" />}
            label="Refund or cancellation request"
            onClick={onShowRefundRequest}
          />
        )}
      </TimelineHeaderAddMenu>

      {/* Phone call with phone dropdown */}
      <ActionButtonGroup
        primaryButton={{
          icon: <IconPhoneOutgoing size={16} />,
          onClick: () => {
            // Track call initiated from patient timeline header
            if (patient?.id) {
              onCallInitiated(patient.id);
            }
            const phoneNumber = getPrimaryPhone(patientPhones)?.value ?? patientPhones[0]?.value ?? '';
            onMakeCall(phoneNumber);
          },
          tooltip: 'Call patient',
        }}
        dropdownItems={callPhoneItems}
        dropdownTooltip="Select a phone"
      />
    </Group>
  );
}

// Note: "+ Add" dropdown menu item is shared via TimelineHeaderAddMenuItem.
